/**
 * Member Revenue chart — card 4530 (BOARD.md).
 *
 * ⛔ THIS FILE CONTAINS NO ARITHMETIC. Every number it renders is derived in
 * `web/utils/memberRevenueModel.js`, which is a `.js` precisely so jest can import it
 * (`jest.config.js` runs `transform: {}` with `moduleFileExtensions: ['js','mjs','json']`, so a
 * `.jsx` is unreachable from a unit test). If you find yourself needing `/ 100` here, the helper
 * belongs in the model.
 *
 * 🔴 TWO AXES, NOT ONE. Money is plotted on `yAxisId="dollars"` (left) and headcount on
 * `yAxisId="members"` (right). They differ by orders of magnitude on live data — a $46k series
 * flattens every count onto the axis floor on a shared axis, which reads as "the member counts are
 * missing". Each series element therefore carries its own `yAxisId`; the suite counts each id
 * TWICE over comment-stripped code (once on the axis, once on the series) and goes red if a
 * series drops it.
 *
 * ⛔ THE EMPTY STATE IS A REAL NODE, NOT A RENDER GUARD. `{rows.length > 0 && <Chart/>}` turns a
 * data bug into an invisible one — card 4306 lost an entire panel that way. When there is nothing
 * to plot this renders `member-revenue-empty`, which a screenshot and a selector can both find.
 *
 * ⛔ NO PAGE FILE IS TOUCHED HERE. Card #4531 mounts this component; that is the file-conflict
 * boundary between the two cards and it is why they are separate.
 */
import { memo, useMemo } from 'react';
import {
  LineChart, Line, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

import {
  toChartRows,
  toSummaryRows,
  toUnresolvedNotice,
  toExpectedCollected,
  hasMemberRevenueData,
  readTotalRow,
  formatMemberRevenueDollarTick,
  formatMemberRevenueCount,
  MEMBER_REVENUE_TOTAL_KEY
} from '../../utils/memberRevenueModel.js';
import styles from './MemberRevenueChart.module.css';

/** Series colours: money green, headcount blue. Named once so legend, line and swatch agree. */
const MONEY_COLOR = '#22c55e';
const MEMBERS_COLOR = '#3b82f6';

const MONEY_NAME = 'Projected monthly dues';
const MEMBERS_NAME = 'Active members';

const AXIS_TICK = { fontSize: 11, fill: 'var(--color-gray-500, #737373)' };

/** Axis tick formatters. Both are model functions passed straight through — no unit conversion here. */
const formatDollarTick = formatMemberRevenueDollarTick;
const formatCountTick = formatMemberRevenueCount;

/**
 * The tooltip names its unit on every line. `Members: 1,941` / `Est. revenue: $46,121` — a bare
 * pair of numbers on a dual-axis chart is exactly what makes one get read as the other.
 */
const MemberRevenueTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className={styles.tooltip} data-testid="member-revenue-tooltip">
      <p className={styles.tooltipTitle}>{row.label}</p>
      {row.agencyName ? <p className={styles.tooltipAgency}>{row.agencyName}</p> : null}
      <p className={styles.tooltipLine}>
        <span className={styles.swatch} style={{ background: MEMBERS_COLOR }} />
        {MEMBERS_NAME}: {row.membersLabel}
      </p>
      <p className={styles.tooltipLine}>
        <span className={styles.swatch} style={{ background: MONEY_COLOR }} />
        {MONEY_NAME}: {row.money}
      </p>
      {row.sourceBadge ? <p className={styles.tooltipSource}>source: {row.sourceBadge}</p> : null}
    </div>
  );
};

/** The two REQ-10 stats. Separately labelled, never summed, never adjacent without their labels. */
const ExpectedCollected = ({ expected, collected }) => (
  <div className={styles.stats} data-testid="member-revenue-stats">
    <div className={styles.stat} data-testid="member-revenue-expected">
      <span className={styles.statLabel}>{expected.label}</span>
      <span className={styles.statValue}>{expected.money}</span>
    </div>
    {collected.shouldDisplay ? (
      <div
        className={styles.stat}
        data-testid="member-revenue-collected"
        data-available={String(collected.available)}
      >
        <span className={styles.statLabel}>{collected.label}</span>
        <span className={styles.statValue}>{collected.money}</span>
      </div>
    ) : null}
  </div>
);

/** One summary row. The total row is the same component with different styling and a testid. */
const SummaryRow = ({ row }) => (
  <div
    className={row.isTotal ? `${styles.summaryRow} ${styles.totalRow}` : styles.summaryRow}
    data-testid={row.isTotal ? 'member-revenue-total' : 'member-revenue-summary-row'}
    data-reconciles={row.isTotal ? String(row.reconciles) : undefined}
    data-source={row.sourceBadge ?? undefined}
  >
    <span className={styles.summaryLabel}>{row.label}</span>
    <span className={styles.summaryAgency}>{row.agencyName ?? ''}</span>
    <span className={styles.summaryMembers}>{row.membersLabel}</span>
    <span className={styles.summaryMoney}>{row.money}</span>
    <span className={styles.summaryBadge}>
      {row.sourceBadge ? <span className={styles.badge}>{row.sourceBadge}</span> : null}
    </span>
  </div>
);

/**
 * `payload` is the `GET /api/admin/analytics/member-revenue` response (card 4529), which is
 * card 4528's ladder payload plus an optional `collected` block.
 */
const MemberRevenueChart = ({ payload = null, loading = false }) => {
  const rows = useMemo(() => toChartRows(payload), [payload]);
  const summaryRows = useMemo(() => toSummaryRows(payload), [payload]);
  const unresolved = useMemo(() => toUnresolvedNotice(payload), [payload]);
  const stats = useMemo(() => toExpectedCollected(payload), [payload]);
  const hasData = useMemo(() => hasMemberRevenueData(payload), [payload]);

  const totalRow = useMemo(() => readTotalRow(summaryRows), [summaryRows]);

  return (
    <section
      className={styles.card}
      data-testid="member-revenue-card"
      data-role="chart-card"
      data-chunk-count={rows.length}
      data-estimated-count={unresolved.estimatedMembers}
      data-unresolved-count={unresolved.unresolvedMembers}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>Member Revenue</h2>
        <p className={styles.subtitle}>
          How membership growth translates into projected monthly dues
        </p>
      </header>

      <div className={styles.formula} data-testid="member-revenue-formula">
        <div className={styles.formulaTerm}>
          <span className={styles.formulaLabel}>Active members</span>
          <span className={styles.formulaDetail}>People with an active membership</span>
        </div>
        <span className={styles.formulaOperator} aria-hidden="true">×</span>
        <div className={styles.formulaTerm}>
          <span className={styles.formulaLabel}>Each member’s monthly rate</span>
          <span className={styles.formulaDetail}>Their contracted, tier, list, or estimated rate</span>
        </div>
        <span className={styles.formulaOperator} aria-hidden="true">=</span>
        <div className={`${styles.formulaTerm} ${styles.formulaResult}`}>
          <span className={styles.formulaLabel}>Projected monthly dues</span>
          <span className={styles.formulaDetail}>Not confirmed cash collected</span>
        </div>
      </div>

      <p className={styles.readingNote}>
        If the plan mix stays the same, members and projected dues rise together. They separate only
        when the mix of monthly rates changes.
      </p>

      <ExpectedCollected expected={stats.expected} collected={stats.collected} />

      {loading ? (
        <div className={styles.empty} data-testid="member-revenue-empty" data-reason="loading">
          <p>Loading member revenue…</p>
        </div>
      ) : null}

      {!loading && !hasData ? (
        <div className={styles.empty} data-testid="member-revenue-empty" data-reason="no-data">
          <p>No member revenue to display.</p>
          <p className={styles.emptyHint}>
            No membership plan or custom tier resolved for any active member.
          </p>
        </div>
      ) : null}

      {!loading && hasData ? (
        <div className={styles.chart} data-testid="member-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 18, right: 12, bottom: 8, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-tan-300, #f3e5d0)" />
              <XAxis
                dataKey="label"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={64}
              />
              <YAxis
                yAxisId="dollars"
                orientation="left"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={72}
                tickFormatter={formatDollarTick}
              />
              <YAxis
                yAxisId="members"
                orientation="right"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={52}
                allowDecimals={false}
                tickFormatter={formatCountTick}
              />
              <Tooltip content={<MemberRevenueTooltip />} cursor={{ stroke: 'var(--color-tan-400, #e8d4b8)' }} />
              <Legend verticalAlign="top" height={28} iconType="line" />
              <Line
                yAxisId="dollars"
                dataKey="expectedMonthlyDollars"
                name={MONEY_NAME}
                type="linear"
                stroke={MONEY_COLOR}
                strokeWidth={2}
                dot={{ r: 2.25, strokeWidth: 1.25, fill: '#ffffff' }}
                activeDot={{ r: 4 }}
              >
                <LabelList dataKey="moneyCompact" position="top" className={styles.pointLabel} />
              </Line>
              <Line
                yAxisId="members"
                dataKey="activeMembers"
                name={MEMBERS_NAME}
                type="linear"
                stroke={MEMBERS_COLOR}
                strokeWidth={2}
                dot={{ r: 2.25, strokeWidth: 1.25, fill: '#ffffff' }}
                activeDot={{ r: 4 }}
              >
                <LabelList dataKey="membersLabel" position="top" className={styles.pointLabel} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      <div className={styles.summary} data-testid="member-revenue-summary">
        <div className={`${styles.summaryRow} ${styles.summaryHead}`}>
          <span className={styles.summaryLabel}>Plan / tier</span>
          <span className={styles.summaryAgency}>Agency</span>
          <span className={styles.summaryMembers}>Members</span>
          <span className={styles.summaryMoney}>Projected monthly dues</span>
          <span className={styles.summaryBadge}>Source</span>
        </div>
        {summaryRows.map((row) => (
          <SummaryRow key={row.key ?? MEMBER_REVENUE_TOTAL_KEY} row={row} />
        ))}
      </div>

      {totalRow && totalRow.reconciles === false ? (
        <p className={styles.mismatch} data-testid="member-revenue-mismatch">
          Published total {totalRow.money} does not equal the sum of the rows above
          ({totalRow.chunkSumMoney}) — reported, not re-derived.
        </p>
      ) : null}

      <p
        className={styles.unresolved}
        data-testid="member-revenue-unresolved"
        data-unresolved-count={unresolved.unresolvedMembers}
        data-list-price-count={unresolved.listPriceMembers}
      >
        {unresolved.text} · {unresolved.fallbackText}
      </p>
    </section>
  );
};

export default memo(MemberRevenueChart);
